import { MouseEvent, useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AxiosError } from 'axios';
import moment from 'moment/moment';

import { CfsApi, MasterNameApi } from '@/api';
import { IcArrowDown, ICCareLinkLogo, IcCheckSuccess } from '@/assets/images';
import { Avatar, Chip, DurationCell, PageHeader } from '@/components';
import { MenuItemWithDoubleClickPrevention } from '@/components/Buttons/MenuItemWithDoubleClickPrevention';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import { withLoader, WithLoaderProps } from '@/hocs';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import {
  AssignedUnitShiftStatus,
  AssignmentStatus,
  IAccountSettings,
  IUser,
  MasterNameType,
} from '@/models';
import CFSPrintModal from '@/pages/CFS/CFSPrintModal/CFSPrintModal';
import { CFSActiveViewers } from '@/pages/CFS/Edit/components/CFSActiveViewers';
import UpdateDispositionModal from '@/pages/CFS/Edit/components/UpdateDispositionModal';
import UpdatePrimaryOfficerModal from '@/pages/CFS/Edit/components/UpdatePrimaryOfficerModal';
import {
  loadActiveCFSListRoute,
  loadCFSListRoute,
} from '@/pages/CFS/loadCFSRoutes';
import { canModifyCFS } from '@/services';
import {
  ConfirmTypes,
  useDataStore,
  useToastStore,
  useUserStore,
} from '@/store';
import colors from '@/theme/variables/colors';
import { getNewTabSearch, getTimeDifference, isOpenedInNewTab } from '@/utils';

import { CFSAssignmentStatusItem } from './CFSAssignmentStatusItem';
import { CFSNavigationArrows } from './CFSNavigationArrows';
import ClosureReasonModal from './ClosureReasonModal';
import ResendNotificationModal from './ResendNotificationModal';
import { CFSCreateRmsMenuItem } from '../../List/components/CFSCreateRmsMenuItem';

const CFSHeader = ({
  hideLoader,
  showLoader,
  isQuickView,
  accountSettings,
  enableActiveUsers,
  ...rest
}: {
  cfsId?: string;
  isQuickView?: boolean;
  accountSettings: IAccountSettings | undefined;
  enableActiveUsers?: boolean;
} & WithLoaderProps) => {
  const urlParams = useParams();
  const cfsId = rest.cfsId || String(urlParams.cfsId);
  const { cfs, ablyCfsChannel, updateCFS } = useCFSContext();
  const { updateToast } = useToastStore();
  const { cfsCloseReasons } = useDataStore();
  const { account, user } = useUserStore();
  // const { handlePrint: handleCFSPrint } = useCFSPrint();
  const { backRoute } = useUnitShiftContext();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const { searchQuery } = useSearchPersist();

  // Hide back button only for standalone/new-window contexts where no back route exists.
  const isOpenedInNewTabMode = isOpenedInNewTab(searchParams);
  const isFromCfsListPage =
    pathname.startsWith('/cfs/') &&
    !pathname.includes('/cfs-active') &&
    !isOpenedInNewTabMode;
  const shouldShowBackForFullPage =
    !isQuickView && !isOpenedInNewTabMode && !!backRoute;

  const isEnabledUnderFireFeature =
    account?.settings?.features?.isEnabledUnderFire;

  const isEnabledNursingHome =
    account?.settings?.features?.isEnabledNursingHome;

  const [openUpdatePrimaryOfficerModal, setOpenPrimaryOfficerModal] =
    useState(false);
  const [openDispositionModal, setOpenDispositionModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [pendingCloseRedirect, setPendingCloseRedirect] = useState<
    string | null
  >(null);
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [, setOrganizationName] = useState<string>('');
  const [openCfsPrintModal, setOpenCfsPrintModal] = useState<boolean>(false);

  const fullName = cfs?.createdBy?.fullName ?? '';
  const openActionsMenu = Boolean(anchorEl);
  const closedReason = cfs?.isClosed
    ? cfsCloseReasons?.find(({ _id }) => _id === cfs.closeReason)
    : null;
  const showRTDButton =
    (!account?.settings.autoCFSRTD ||
      (cfs?.incidentCodes &&
        cfs.incidentCodes.length > 0 &&
        cfs?.incidentCodes.every(
          (incidentCode) => incidentCode.emergency === false,
        ))) &&
    !cfs?.isReadyToDispatch &&
    cfs?.addressInfo?.address?._id &&
    cfs.assignmentStatus === AssignmentStatus.NEW;

  const theme = useTheme();
  const navigate = useNavigate();
  const hasOnSceneUnit = cfs?.assignedUnitShifts?.find(
    (assignedUnit) => assignedUnit.status === AssignedUnitShiftStatus.ON_SCENE,
  );

  const canMarkFireUnderControl =
    !cfs?.fireControlled &&
    accountSettings?.features?.isEnabledUnderFire &&
    hasOnSceneUnit;

  useEffect(() => {
    if (cfs?.assignmentStatus) {
      ablyCfsChannel?.publish('', { assignmentStatus: cfs.assignmentStatus });
    }
  }, [cfs?.assignmentStatus]);

  useEffect(() => {
    if (cfs?.reporter?.callerPhoneNumber) {
      handleOrganizationPhoneNumber(cfs?.reporter.callerPhoneNumber);
    }
  }, [cfs?.reporter?.callerPhoneNumber]);

  const handleOrganizationPhoneNumber = async (pNumber: string) => {
    try {
      const filter = {
        phoneNumbers: {
          $elemMatch: {
            phoneNumber: pNumber,
          },
        },
        type: MasterNameType.ORGANIZATION,
      };

      const { data }: any = await MasterNameApi.getMasterNames({
        filter: JSON.stringify(filter),
        limit: 1,
      });

      setOrganizationName(data.results?.[0]?.name || '');
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };
  const handleCloseCFS = () => {
    setOpenModal(true);
    handleCloseActionsMenu();
  };

  const handleReopenCFS = () => {
    if (cfsId) {
      CfsApi.reopenCFS(cfsId)
        .then(() => {
          updateCfsClosedState(false, '');
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: 'CFS record re-opened successfully',
          });
        })
        .catch((error: AxiosError) => {
          updateToast({
            type: ConfirmTypes.ERROR,
            open: true,
            message: `ERROR,CFS record CANNOT be re-opened: ${error.message}`,
          });
        })
        .finally(() => setAnchorEl(null));
    }
  };

  const handleResendDispatchNotification = () => {
    setOpenNotificationModal(true);
  };

  const onOpenDispositionClose = () => {
    setOpenDispositionModal(false);
  };

  const onClose = () => {
    setOpenModal(false);
  };

  const preloadClosedSuccessRoute = async () => {
    const targetRoute = backRoute ?? '/cfs-active';

    if (targetRoute.startsWith('/cfs-active')) {
      await loadActiveCFSListRoute();
      return;
    }

    if (targetRoute.startsWith('/cfs')) {
      await loadCFSListRoute();
    }
  };

  const onClosedSuccess = () => {
    const search = getNewTabSearch(searchParams);
    localStorage.removeItem('cfs-back-route');
    setPendingCloseRedirect(`${backRoute ?? '/cfs-active'}${search}`);
  };

  useEffect(() => {
    if (openModal || !pendingCloseRedirect) return;

    navigate(pendingCloseRedirect, { replace: true });
    setPendingCloseRedirect(null);
  }, [navigate, openModal, pendingCloseRedirect]);

  const onCloseNotificationModal = () => {
    setOpenNotificationModal(false);
    setAnchorEl(null);
  };

  const handleOpenActionsMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUpdatePrimaryOfficer = () => {
    setOpenPrimaryOfficerModal(true);
    setAnchorEl(null);
  };

  const handleUpdatePrimaryOfficerClose = () => {
    setOpenPrimaryOfficerModal(false);
  };

  const handleCloseActionsMenu = () => {
    setAnchorEl(null);
  };

  const handleCreateNewCFS = async () => {
    try {
      const response = await CfsApi.create();
      window.location.replace(
        `${window.location.origin}/cfs/${response.data._id}`,
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleUpdateDisposition = async () => {
    setOpenDispositionModal(true);
    setAnchorEl(null);
  };

  const updateCfsClosedState = (isClosed: boolean, closeReason: string) => {
    if (!cfs) return;
    updateCFS({ ...cfs, isClosed, closeReason });
  };

  const setCfsRTD = async () => {
    try {
      showLoader();
      await CfsApi.setCFSRTD(cfsId ?? '');
      if (cfs) updateCFS({ ...cfs, isReadyToDispatch: true });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const handleFireUnderControl = async () => {
    try {
      showLoader();
      const responseCfs = await CfsApi.makeFireUnderControl(cfsId ?? '');
      if (cfs)
        updateCFS({ ...cfs, fireControlled: responseCfs.data.fireControlled });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const renderStatusChip = () => {
    if (cfs?.isClosed) {
      return (
        <Chip
          label="Closed"
          className="cfs-assignment-status"
          sx={{
            backgroundColor:
              theme.palette.mode === 'light'
                ? colors.grey[10]
                : theme.palette.background.default,
            color:
              theme.palette.mode === 'light'
                ? colors.grey[90]
                : theme.palette.text.primary,
            border:
              theme.palette.mode === 'dark'
                ? `1px solid ${theme.palette.divider}`
                : undefined,
          }}
        />
      );
    } else {
      if (isEnabledUnderFireFeature && cfs?.fireControlled) {
        return (
          <Chip
            label="Fire controlled"
            sx={{
              backgroundColor: '#97CC6D',
              color: theme.palette.getContrastText('#97CC6D'),
            }}
          />
        );
      } else if (!cfs?.fireControlled && cfs?.assignmentStatus) {
        return (
          <CFSAssignmentStatusItem
            assignmentStatus={cfs.assignmentStatus}
            isReadyToDispatch={cfs.isReadyToDispatch}
            className="cfs-assignment-status"
          />
        );
      } else {
        return null;
      }
    }
  };

  const renderMarkFireControlledButton = () => (
    <>
      {canModifyCFS(user as IUser) && canMarkFireUnderControl && (
        <Button
          onClick={handleFireUnderControl}
          color="error"
          variant="text"
          size="small"
          style={{ textDecoration: 'underline' }}
        >
          Mark Fire Under Control
        </Button>
      )}
    </>
  );

  const renderRFDButton = () => (
    <>
      {canModifyCFS(user as IUser) && showRTDButton && (
        <Button
          variant="text"
          color="error"
          sx={{ ml: -2, textDecoration: 'underline' }}
          onClick={setCfsRTD}
        >
          Mark as Ready for Dispatch
        </Button>
      )}
    </>
  );

  const renderCareLinkLogo = () => {
    const presetCareLinkCfsIds = [
      '100000000000000000000001',
      '200000000000000000000002',
    ];

    if (
      cfs?._id &&
      isEnabledNursingHome &&
      presetCareLinkCfsIds.includes(cfs?._id)
    ) {
      return (
        <>
          <Stack
            direction="row"
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: '1px solid',
              padding: '4px 8px',
              borderColor: 'divider',
              borderRadius: '8px',
              alignItems: 'center',
              marginLeft: 'auto',
            }}
          >
            <ICCareLinkLogo />
            <Typography
              fontSize={15}
              fontWeight={500}
              paddingLeft={1}
              color="text.primary"
            >
              Initiated by CareLink
            </Typography>
          </Stack>
        </>
      );
    }
  };

  const renderCloseReason = () => (
    <>
      {!!closedReason && (
        <Chip
          label={closedReason.reason}
          className="cfs-assignment-status"
          sx={{
            backgroundColor:
              theme.palette.mode === 'light'
                ? colors.grey[10]
                : theme.palette.background.default,
            color:
              theme.palette.mode === 'light'
                ? colors.grey[90]
                : theme.palette.text.primary,
            border:
              theme.palette.mode === 'dark'
                ? `1px solid ${theme.palette.divider}`
                : undefined,
          }}
        />
      )}
    </>
  );

  const renderEmailSentAt = () => (
    <>
      {cfs?.emailSentAt && (
        <Tooltip
          title={`Last email sent at: ${new Date(
            cfs?.emailSentAt,
          ).toLocaleDateString()}`}
        >
          <Box sx={{ mt: 0.75 }}>
            <IcCheckSuccess />
          </Box>
        </Tooltip>
      )}
    </>
  );

  const renderDuration = () => {
    if (!cfs) {
      return <></>;
    }

    // Closed CFS should not display a live/ticking timer.
    if (cfs.isClosed) {
      return null;
    }

    const isOnScene = cfs.assignedUnitShifts
      ?.filter((unit) => unit.status === AssignedUnitShiftStatus.ON_SCENE)
      ?.sort(
        (a, b) => moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
      )?.[0];

    const date = cfs.createdAt;
    const originTime = new Date(date);

    const preferred = isOnScene
      ? getTimeDifference(moment(isOnScene.updatedAt), originTime).format(
          'HH:mm:ss',
        )
      : undefined;

    return (
      <Tooltip title="Live Timer">
        <Box marginLeft="auto">
          <DurationCell
            date={date}
            preferred={preferred}
            sx={{
              paddingX: '6px',
              paddingY: '4px',
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Box
        sx={{
          '& .cfs-navigation-arrows': {
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
          },
          '&:hover .cfs-navigation-arrows': {
            opacity: 1,
          },
        }}
      >
        <PageHeader
          forceHideBackButton={
            !isQuickView && !isFromCfsListPage && !shouldShowBackForFullPage
          }
          onBack={
            isQuickView
              ? () => {
                  const search = getNewTabSearch(searchParams);
                  navigate(`${backRoute ?? '/cfs-active'}${search}`);
                  localStorage.removeItem('cfs-back-route');
                }
              : undefined
          }
          backRoute={backRoute ? `${backRoute}${searchQuery}` : backRoute}
        >
          <Stack flex={1} direction="row" alignItems="center" columnGap={2.5}>
            {canModifyCFS(user as IUser) && (
              <Button
                variant="contained"
                color="error"
                onClick={handleOpenActionsMenu}
                endIcon={<IcArrowDown />}
                className="cfs-action-button"
              >
                Actions
              </Button>
            )}

            <Box className="cfs-navigation-arrows">
              <CFSNavigationArrows cfsId={cfsId} />
            </Box>

            <Typography variant="inherit" sx={{ fontSize: '1.313rem' }}>
              {cfs?.number}
            </Typography>

            {renderStatusChip()}
            {renderCloseReason()}
            {renderEmailSentAt()}
            {renderMarkFireControlledButton()}
            {renderRFDButton()}
            {renderCareLinkLogo()}
            {renderDuration()}
          </Stack>

          <Stack direction="row" alignItems="center" columnGap={2.5}>
            {enableActiveUsers && (
              <CFSActiveViewers cfsId={cfs?._id} account={account} />
            )}
            <Divider orientation="vertical" flexItem />

            {!!fullName && (
              <Avatar
                className="cfs-detail-avatar"
                name={fullName}
                showTooltip
              />
            )}
          </Stack>
        </PageHeader>
      </Box>

      {openUpdatePrimaryOfficerModal && cfs?._id && (
        <UpdatePrimaryOfficerModal
          cfsId={cfs?._id}
          open={openUpdatePrimaryOfficerModal}
          onClose={handleUpdatePrimaryOfficerClose}
          officer={
            typeof cfs?.primaryOfficer === 'string'
              ? cfs?.primaryOfficer
              : cfs?.primaryOfficer?._id
          }
        />
      )}

      {openDispositionModal && cfs?._id && (
        <UpdateDispositionModal
          cfsId={cfs?._id}
          open={openDispositionModal}
          onClose={onOpenDispositionClose}
          reason={cfs.closeReason as string} // if the cfs is closed, there is a close reason.
        />
      )}

      {openModal && cfs?._id && (
        <ClosureReasonModal
          cfsId={cfs?._id}
          open={openModal}
          onClose={onClose}
          onBeforeClosedSuccess={preloadClosedSuccessRoute}
          onClosedSuccess={onClosedSuccess}
          updateCfsClosedState={updateCfsClosedState}
          assignedAgencies={cfs.assignedAgencies ?? []}
        />
      )}

      {openNotificationModal && cfs?._id && (
        <ResendNotificationModal
          cfsId={cfs?._id}
          open={openNotificationModal}
          onClose={onCloseNotificationModal}
        />
      )}

      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={openActionsMenu}
        onClose={handleCloseActionsMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {canModifyCFS(user as IUser) && (
          <MenuItemWithDoubleClickPrevention onClick={handleCreateNewCFS}>
            Save and New
          </MenuItemWithDoubleClickPrevention>
        )}
        {canModifyCFS(user as IUser) && (
          <CFSCreateRmsMenuItem cfsId={cfs?._id} />
        )}

        {canModifyCFS(user as IUser) &&
          (cfs?.isClosed ? (
            <>
              <MenuItem onClick={handleReopenCFS}>ReOpen CFS record</MenuItem>
              <MenuItem onClick={handleUpdateDisposition}>
                Update disposition
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem onClick={handleUpdatePrimaryOfficer}>
                Update primary officer
              </MenuItem>
              <MenuItem onClick={handleCloseCFS}>Close CFS record</MenuItem>
            </>
          ))}
        {canModifyCFS(user as IUser) &&
        cfs?.isReadyToDispatch &&
        !cfs?.isClosed ? (
          <MenuItem onClick={handleResendDispatchNotification}>
            Resend App Notification
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            setOpenCfsPrintModal(true);
            handleCloseActionsMenu();
          }}
        >
          Print
        </MenuItem>
      </Menu>

      <CFSPrintModal
        openPrintModal={openCfsPrintModal}
        setOpenPrintModal={setOpenCfsPrintModal}
        cfsId={cfsId}
      />
    </>
  );
};

export default withLoader(CFSHeader);
